import React, { useState, useEffect } from 'react';
import {
  X,
  Bookmark,
  Heart,
  Star,
  MapPin,
  Clock,
  Plus,
  Trash2,
  Edit3,
  Share2,
  Filter,
  Search,
  Grid,
  List,
  Calendar,
  FolderPlus,
  Folder,
  ChevronRight,
  Eye,
  ExternalLink
} from 'lucide-react';
import { Business, User } from '../types';

interface BookmarkList {
  id: string;
  name: string;
  description?: string;
  businessIds: string[];
  createdAt: Date;
  isPrivate: boolean;
  color: string;
}

interface BookmarkManagerProps {
  user: User;
  businesses: Business[];
  onClose: () => void;
  onViewBusiness: (business: Business) => void;
}

const BookmarkManager: React.FC<BookmarkManagerProps> = ({
  user,
  businesses,
  onClose,
  onViewBusiness
}) => {
  const [activeTab, setActiveTab] = useState<'bookmarks' | 'favorites' | 'lists'>('bookmarks');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedList, setSelectedList] = useState<string | null>(null);
  const [showCreateList, setShowCreateList] = useState(false);
  const [filterCategory, setFilterCategory] = useState<string>('all');

  // Sample data - in a real app, this would come from API/localStorage
  const [bookmarkedBusinesses] = useState<string[]>(['1', '3', '5']);
  const [favoritedBusinesses] = useState<string[]>(['1', '2', '4']);
  const [bookmarkLists, setBookmarkLists] = useState<BookmarkList[]>([
    {
      id: 'list1',
      name: 'Coffee Spots',
      description: 'My favorite places for coffee and work',
      businessIds: ['1', '3'],
      createdAt: new Date('2024-01-15'),
      isPrivate: false,
      color: 'bg-blue-500'
    },
    {
      id: 'list2',
      name: 'Date Night',
      description: 'Romantic restaurants and activities',
      businessIds: ['2', '4'],
      createdAt: new Date('2024-01-10'),
      isPrivate: true,
      color: 'bg-pink-500'
    },
    {
      id: 'list3',
      name: 'Weekend Adventures',
      businessIds: ['5'],
      createdAt: new Date('2024-01-05'),
      isPrivate: false,
      color: 'bg-green-500'
    }
  ]);

  const getBookmarkedBusinesses = () => {
    return businesses.filter(business => bookmarkedBusinesses.includes(business.id));
  };

  const getFavoritedBusinesses = () => {
    return businesses.filter(business => favoritedBusinesses.includes(business.id));
  };

  const getFilteredBusinesses = (businessList: Business[]) => {
    let filtered = businessList;

    if (searchQuery) {
      filtered = filtered.filter(business =>
        business.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        business.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        business.address.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (filterCategory !== 'all') {
      filtered = filtered.filter(business =>
        business.category.toLowerCase() === filterCategory.toLowerCase()
      );
    }

    return filtered;
  };

  const getCategories = (businessList: Business[]) => {
    const categories = new Set(businessList.map(business => business.category));
    return Array.from(categories);
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < Math.floor(rating) 
            ? 'text-yellow-400 fill-current' 
            : 'text-gray-300'
        }`}
      />
    ));
  };

  const renderBusinessCard = (business: Business, showListActions = false) => (
    <div key={business.id} className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200 ${
      viewMode === 'list' ? 'flex' : ''
    }`}>
      {/* Business Image */}
      <div className={`bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center ${
        viewMode === 'list' ? 'w-24 h-24' : 'h-32'
      }`}>
        <span className="text-white text-lg font-bold">
          {business.name.charAt(0)}
        </span>
      </div>

      <div className={`p-4 ${viewMode === 'list' ? 'flex-1' : ''}`}>
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 text-sm mb-1">{business.name}</h3>
            <p className="text-xs text-gray-600 mb-2">{business.category}</p>
            
            <div className="flex items-center space-x-1 mb-2">
              <div className="flex">{renderStars(business.averageRating || 0)}</div>
              <span className="text-xs text-gray-600">
                ({business.totalReviews})
              </span>
            </div>

            <div className="flex items-center space-x-1 text-xs text-gray-600">
              <MapPin className="w-3 h-3" />
              <span className="truncate">{business.address}</span>
            </div>
          </div>

          {showListActions && (
            <div className="flex items-center space-x-1 ml-2">
              <button className="p-1 text-gray-400 hover:text-gray-600">
                <Edit3 className="w-4 h-4" />
              </button>
              <button className="p-1 text-gray-400 hover:text-red-600">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between">
          <button
            onClick={() => onViewBusiness(business)}
            className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center space-x-1"
          >
            <Eye className="w-3 h-3" />
            <span>View Details</span>
          </button>

          <div className="flex items-center space-x-1">
            <button className="p-1 text-gray-400 hover:text-gray-600">
              <Share2 className="w-4 h-4" />
            </button>
            <button className="p-1 text-gray-400 hover:text-gray-600">
              <ExternalLink className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderBookmarksTab = () => {
    const bookmarked = getFilteredBusinesses(getBookmarkedBusinesses());
    const categories = getCategories(getBookmarkedBusinesses());

    return (
      <div className="space-y-4">
        {/* Filters */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {viewMode === 'grid' ? <List className="w-4 h-4" /> : <Grid className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Business Grid/List */}
        {bookmarked.length > 0 ? (
          <div className={viewMode === 'grid' ? 'grid grid-cols-2 gap-4' : 'space-y-3'}>
            {bookmarked.map(business => renderBusinessCard(business))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Bookmark className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Bookmarks Yet</h3>
            <p className="text-gray-600 mb-4">Start bookmarking businesses you want to visit later</p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700"
            >
              Explore Businesses
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderFavoritesTab = () => {
    const favorites = getFilteredBusinesses(getFavoritedBusinesses());

    return (
      <div className="space-y-4">
        {favorites.length > 0 ? (
          <div className={viewMode === 'grid' ? 'grid grid-cols-2 gap-4' : 'space-y-3'}>
            {favorites.map(business => renderBusinessCard(business))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Heart className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Favorites Yet</h3>
            <p className="text-gray-600 mb-4">Mark businesses as favorites to find them easily</p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700"
            >
              Explore Businesses
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderListsTab = () => {
    if (selectedList) {
      const list = bookmarkLists.find(l => l.id === selectedList);
      if (!list) return null;

      const listBusinesses = businesses.filter(business => 
        list.businessIds.includes(business.id)
      );

      return (
        <div className="space-y-4">
          {/* List Header */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSelectedList(null)}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
            >
              <ChevronRight className="w-4 h-4 rotate-180" />
              <span>Back to Lists</span>
            </button>

            <div className="flex items-center space-x-2">
              <button className="p-2 text-gray-400 hover:text-gray-600">
                <Edit3 className="w-4 h-4" />
              </button>
              <button className="p-2 text-gray-400 hover:text-gray-600">
                <Share2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className={`p-4 rounded-lg ${list.color} bg-opacity-10 border-l-4 ${list.color}`}>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">{list.name}</h2>
            {list.description && (
              <p className="text-sm text-gray-600 mb-2">{list.description}</p>
            )}
            <div className="flex items-center space-x-4 text-xs text-gray-600">
              <span>{list.businessIds.length} businesses</span>
              <span>Created {list.createdAt.toLocaleDateString()}</span>
              {list.isPrivate && <span>Private</span>}
            </div>
          </div>

          {/* List Businesses */}
          {listBusinesses.length > 0 ? (
            <div className={viewMode === 'grid' ? 'grid grid-cols-2 gap-4' : 'space-y-3'}>
              {listBusinesses.map(business => renderBusinessCard(business, true))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Folder className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600">This list is empty</p>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Create New List Button */}
        <button
          onClick={() => setShowCreateList(true)}
          className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-colors duration-200"
        >
          <div className="flex items-center justify-center space-x-2">
            <FolderPlus className="w-5 h-5" />
            <span>Create New List</span>
          </div>
        </button>

        {/* Existing Lists */}
        <div className="space-y-3">
          {bookmarkLists.map(list => (
            <div
              key={list.id}
              onClick={() => setSelectedList(list.id)}
              className="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md cursor-pointer transition-shadow duration-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 ${list.color} rounded-lg flex items-center justify-center`}>
                    <Folder className="w-5 h-5 text-white" />
                  </div>
                  
                  <div>
                    <h3 className="font-medium text-gray-900">{list.name}</h3>
                    <p className="text-sm text-gray-600 flex items-center space-x-2">
                      <span>{list.businessIds.length} businesses</span>
                      {list.isPrivate && (
                        <span className="px-2 py-0.5 bg-gray-100 text-xs rounded-full">Private</span>
                      )}
                    </p>
                  </div>
                </div>
                
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
              
              {list.description && (
                <p className="text-sm text-gray-600 mt-2">{list.description}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h1 className="text-xl font-bold text-gray-900">My Collections</h1>
            <p className="text-sm text-gray-600">Manage your saved businesses and lists</p>
          </div>
          
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-6 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search your saved businesses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex">
            {[
              { id: 'bookmarks', label: 'Bookmarks', icon: Bookmark, count: bookmarkedBusinesses.length },
              { id: 'favorites', label: 'Favorites', icon: Heart, count: favoritedBusinesses.length },
              { id: 'lists', label: 'Lists', icon: Folder, count: bookmarkLists.length }
            ].map(({ id, label, icon: Icon, count }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id as any)}
                className={`flex-1 flex items-center justify-center space-x-2 py-4 px-4 text-sm font-medium border-b-2 transition-colors duration-200 ${
                  activeTab === id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{label}</span>
                <span className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">
                  {count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6 max-h-96 overflow-y-auto">
          {activeTab === 'bookmarks' && renderBookmarksTab()}
          {activeTab === 'favorites' && renderFavoritesTab()}
          {activeTab === 'lists' && renderListsTab()}
        </div>
      </div>
    </div>
  );
};

export default BookmarkManager;
